// Session tracking utility for LTF Life Changer application
// This module handles sending user session data to the admin dashboard
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://mduqbgfmracurnpctppy.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1kdXFiZ2ZtcmFjdXJucGN0cHB5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDg0MTk1NzcsImV4cCI6MjA2Mzk5NTU3N30.4U3sF5n0RQg_LXjus_Q6iXtJPdd1y2mdTFzoulbytfw';

class SessionTracker {
    constructor() {
        this.sessionId = this.getOrCreateSessionId();
        this.supabase = createClient(supabaseUrl, supabaseKey);
        this.trackingEnabled = true;
        
        // Initialize session tracking - made non-blocking
        this.initializeSessionAsync();
    }

    getOrCreateSessionId() {
        let sessionId = sessionStorage.getItem('ltf_session_id');
        if (!sessionId) {
            sessionId = 'ltf_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            sessionStorage.setItem('ltf_session_id', sessionId);
        }
        return sessionId;
    }

    async sendTrackingData(data) {
        if (!this.trackingEnabled) return;

        try {
            // First, try to get existing record with proper headers
            const { data: existingLead, error: fetchError } = await this.supabase
                .from('leads')
                .select('*')
                .eq('session_id', this.sessionId)
                .maybeSingle(); // Use maybeSingle() instead of single() to avoid 406 errors

            if (fetchError && fetchError.code !== 'PGRST116') {
                // Only log in development, don't block the app
                if (process.env.NODE_ENV === 'development') {
                    console.warn('⚠️ Session tracking fetch warning:', fetchError.message);
                }
                return;
            }

            const updateData = {
                session_id: this.sessionId,
                last_activity: new Date().toISOString(),
                ...data
            };

            if (existingLead) {
                // Update existing record
                const { error: updateError } = await this.supabase
                    .from('leads')
                    .update(updateData)
                    .eq('session_id', this.sessionId);

                if (updateError) {
                    // Only log in development
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ Session tracking update warning:', updateError.message);
                    }
                }
            } else {
                // Create new record
                updateData.session_start = new Date().toISOString();
                const { error: insertError } = await this.supabase
                    .from('leads')
                    .insert([updateData]);

                if (insertError) {
                    // Only log in development, and only if it's not a duplicate key error
                    if (process.env.NODE_ENV === 'development' && insertError.code !== '23505') {
                        console.warn('⚠️ Session tracking insert warning:', insertError.message);
                    }
                    // If it's a duplicate key, silently ignore (session already exists)
                }
            }
        } catch (error) {
            // Silent fail for tracking - don't block the app
            if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ Session tracking error:', error.message);
            }
        }
    }

    // Initialize session tracking
    async initializeSessionAsync() {
        try {
            const sessionData = {
                ip_address: await this.getClientIP(),
                user_agent: navigator.userAgent,
                referrer_url: document.referrer || window.location.href,
                current_stage: this.getCurrentStage()
            };

            await this.sendTrackingData(sessionData);
        } catch (error) {
            // Silent fail - don't block the app if tracking fails
            console.warn('Session initialization failed:', error.message);
        }
    }

    // Initialize session when user first visits (legacy method for compatibility)
    async initializeSession() {
        this.initializeSessionAsync();
    }

    // Track OTP verification
    async trackOTPVerification(phoneNumber, countryCode, verificationStatus, otpAttempts = 1) {
        const otpData = {
            phone_number: phoneNumber,
            country_code: countryCode,
            otp_verified: verificationStatus === 'Verified',
            otp_attempts: otpAttempts,
            current_stage: 'otp'
        };

        if (verificationStatus === 'Verified') {
            otpData.otp_verification_time = new Date().toISOString();
        }

        await this.sendTrackingData(otpData);
    }

    // Track user details submission
    async trackUserDetails(userDetails) {
        const detailsData = {
            user_name: userDetails.name,
            company_name: userDetails.companyName,
            industry: userDetails.industry,
            keywords: userDetails.keywords || [],
            tagline: userDetails.tagline,
            form_completed_time: new Date().toISOString(),
            current_stage: 'details'
        };

        await this.sendTrackingData(detailsData);
    }

    // Track details form submission (legacy method)
    async trackDetailsSubmission(userDetails) {
        await this.trackUserDetails(userDetails);
    }

    // Track photo upload
    async trackPhotoUpload(uploadData) {
        const photoData = {
            photo_uploaded: uploadData.success,
            photo_processed: uploadData.status === 'Success',
            face_detected: uploadData.faceDetected || false,
            photo_upload_time: new Date().toISOString(),
            current_stage: 'upload'
        };

        await this.sendTrackingData(photoData);
    }

    // Track original image upload to S3
    async trackOriginalImageS3Upload(s3Data) {
        const imageData = {
            original_image_s3_url: s3Data.url,
            image_upload_type: s3Data.imageType, // 'upload', 'camera', or 'selfie'
            original_image_upload_time: new Date().toISOString()
        };

        await this.sendTrackingData(imageData);
    }

    // Track cropped preview image upload to S3
    async trackCroppedImageS3Upload(s3Data) {
        const croppedData = {
            cropped_image_s3_url: s3Data.url,
            cropped_image_upload_time: new Date().toISOString()
        };

        await this.sendTrackingData(croppedData);
    }

    // Track poster generation
    async trackPosterGeneration(posterData) {
        const generationData = {
            poster_s3_url: posterData.s3Url,
            poster_generated: posterData.status === 'Success',
            poster_generation_time: new Date().toISOString(),
            current_stage: 'poster'
        };

        await this.sendTrackingData(generationData);
    }

    // Track download action
    async trackDownload() {
        const downloadData = {
            poster_downloaded: true,
            poster_download_time: new Date().toISOString()
        };

        await this.sendTrackingData(downloadData);
    }

    // Update session stage
    async updateStage(newStage) {
        await this.sendTrackingData({ current_stage: newStage });
    }

    // Update session stage (new method name)
    async updateSessionStage(newStage) {
        await this.updateStage(newStage);
    }

    // Utility function to determine current stage based on URL
    getCurrentStage() {
        const path = window.location.pathname;
        if (path.includes('otp-verification')) return 'otp';
        if (path.includes('add-details')) return 'details';
        if (path.includes('upload-photo')) return 'upload';
        if (path.includes('share-poster')) return 'poster';
        return 'landing';
    }

    // Get client IP with proper error handling and timeout
    async getClientIP() {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000);
            
            const response = await fetch('https://api.ipify.org?format=json', {
                signal: controller.signal,
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                }
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            return data.ip || 'Unknown';
        } catch (error) {
            if (error.name === 'AbortError') {
                return 'Timeout';
            }
            return 'Unknown';
        }
    }

    // Get session ID for other components
    getSessionId() {
        return this.sessionId;
    }

    // Disable tracking for debugging
    disableTracking() {
        this.trackingEnabled = false;
        console.log('Session tracking disabled');
    }

    // Enable tracking
    enableTracking() {
        this.trackingEnabled = true;
        console.log('Session tracking enabled');
    }
}

// Create global instance
const sessionTracker = new SessionTracker();

export default sessionTracker;